import { useCallback, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { IgPaper } from "@intelligems/ig-theme";
import { AiChatState } from "@intelligems/ig-types";
import { useMediaQuery, useTheme } from "@mui/material";
import { AiAnalyticsChat } from "@src/features/ai/components/AiChat/AiAnalyticsChat";
import { AiChatErrorFallback } from "@src/features/ai/components/AiChat/components/AiChatErrorBoundary";
import { UiActionsChat } from "@src/features/ai/components/AiChat/UiActionsChat";
import { ExperienceCreationSingleAgentChat } from "@src/features/ai/components/AiChat/ExperienceCreationSingleAgentChat";
import { AgenticLogicCreationChat } from "@src/features/ai/components/AiChat/AgenticLogicCreationChat";
import { cancelActiveAgenticLogicCreation } from "@src/features/ai/components/AiChat/agenticLogicCreationController";
import { planAiChatClose } from "@src/features/ai/components/AiChat/aiChatClose";
import { ExecutionPanel } from "@src/features/ai/components/WorkflowEditor/execution/ExecutionPanel";
import { useCallOnUnmount } from "@src/hooks/useCallOnUnmount";
import { useDispatch, useSelector } from "@src/store";
import { clearAiChat } from "@src/store/slices/aiChat";
import { getAiChatProps, setAiChat } from "@src/store/slices/global";
import { SitewideAnalyticsChat } from "@src/features/ai/components/AiChat/SitewideAnalyticsChat";

import { AiChatOverlay } from "./AiChatOverlay";

interface AiChatProps {
  embedded?: boolean;
  historicalChatState?: AiChatState | null;
  onClose?: () => void;
  title?: string;
}

const NOOP_ON_SEND = {
  chat: () => undefined,
  isPending: false,
};

export const AiChat = ({
  embedded = false,
  historicalChatState = null,
  onClose: embeddedOnClose,
  title,
}: AiChatProps = {}) => {
  const { open, mode } = useSelector(getAiChatProps);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const belowLg = useMediaQuery(theme.breakpoints.down("lg"));
  // The Atlas rail is part of the agentic-logic page frame, never a blocking
  // dialog — a narrow window keeps the inline rail rather than swapping to a
  // modal over the page it belongs to.
  const modal = belowLg && mode !== "agentic-logic-creation";
  const dispatch = useDispatch();

  const onClose = useCallback(() => {
    if (embedded) {
      embeddedOnClose?.();
      return;
    }

    const plan = planAiChatClose(mode);
    if (plan.cancelAgenticLogicRun) {
      cancelActiveAgenticLogicCreation("chat-close");
    }
    if (plan.clearTranscript) {
      dispatch(clearAiChat());
    }
    dispatch(setAiChat(plan.nextProps));
  }, [dispatch, embedded, embeddedOnClose, mode]);

  useEffect(() => {
    if (embedded) {
      return;
    }

    if (!open) {
      onClose();
    }
  }, [embedded, onClose, open]);

  // Deliberately NOT folded into the effect above: that one's deps
  // (`onClose`, `open`) change whenever `mode` changes too (e.g. re-opening
  // in a different mode without actually closing first — see
  // `useRedirectOnCategorySwitch`), and a plain `useEffect` cleanup fires on
  // EVERY dependency change, not just true unmount. That would call the
  // STALE `onClose()` (closed over the OLD mode) right after such a switch,
  // immediately closing/clearing the chat it was just switched into.
  // `useCallOnUnmount` only calls it once, at real unmount.
  useCallOnUnmount(() => {
    if (embedded) {
      return;
    }
    onClose();
  });

  if (embedded) {
    if (!historicalChatState) {
      return null;
    }

    return (
      <IgPaper
        className="embedded-ai-chat"
        sx={{
          borderRadius: 2,
          boxSizing: "border-box",
          height: "100%",
          overflow: "hidden",
          width: "100%",
        }}
      >
        <ErrorBoundary
          FallbackComponent={async (props) =>
            AiChatErrorFallback({ onClose, ...props })
          }
          onReset={() => onClose()}
        >
          <ExecutionPanel
            title={title}
            feature={"other"}
            workflowId={historicalChatState.workflowId || "qa-live-debug"}
            onSend={NOOP_ON_SEND}
            injectedChatState={historicalChatState}
            slots={{ chatFooter: null }}
            showSystemMessages={false}
            showToolCallOutput={false}
            showToolsAsTasks={false}
            showConditionNodes={false}
            clearReasoningOnMessage={true}
            showReasoningMessages={true}
          />
        </ErrorBoundary>
      </IgPaper>
    );
  }

  if (!open) {
    return null;
  }

  return (
    <AiChatOverlay fullScreen={fullScreen} modal={modal} onClose={onClose}>
      <ErrorBoundary
        // eslint-disable-next-line @typescript-eslint/promise-function-async
        FallbackComponent={(props) =>
          AiChatErrorFallback({ onClose, ...props })
        }
        onReset={() => onClose()}
      >
        {mode === "analytics" ? (
          <AiAnalyticsChat onClose={onClose} />
        ) : mode === "ui-actions" ? (
          <UiActionsChat onClose={onClose} />
        ) : mode === "sitewide-analytics" ? (
          <SitewideAnalyticsChat onClose={onClose} />
        ) : mode === "experience-creation-single-agent" ? (
          <ExperienceCreationSingleAgentChat onClose={onClose} />
        ) : mode === "agentic-logic-creation" ? (
          // On the agentic-logic surfaces the desktop panel is always visible
          // (`useAtlasChatAlwaysVisible` reopens it), so "Close Atlas" would
          // only flicker-and-reset — the header offers "New chat" for that.
          // The below-`lg` dialog blocks the page and keeps its close button.
          <AgenticLogicCreationChat onClose={modal ? onClose : undefined} />
        ) : null}
      </ErrorBoundary>
    </AiChatOverlay>
  );
};
